import PropTypes from "prop-types";
import { useCallback, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { QUERY_STATUS } from "constants/constants";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_PUBLIC_TEMPLATE_ACTIONS,
  DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES,
  DELIVERY_NOTE_TEMPLATE_ITEM_DETAIL,
} from "constants/forms";
import InfoContext from "pages/InfoContext";
import Icon from "core/icon/Icon";
import LoaderV2 from "core/loader/LoaderV2";
import { Typography } from "core/typography/Typography";
import { countColumnsByType } from "utils/deliveryNoteHelper";
import DeliveryNoteTemplatePreviewModal from "./DeliveryNoteTemplatePreviewModal";

const DeliveryNotePublicTemplateList = ({
  publicTemplates,
  hasActiveFilters,
  loadStatus,
  isBusy,
  onImport,
}) => {
  const intl = useIntl();
  const { confirmAction } = useContext(InfoContext);
  const previewLabel = intl.formatMessage(
    DELIVERY_NOTE_PUBLIC_TEMPLATE_ACTIONS.preview
  );
  const importLabel = intl.formatMessage(
    DELIVERY_NOTE_PUBLIC_TEMPLATE_ACTIONS.import
  );

  const publisherName = (published) =>
    published?.published_by_name ||
    intl.formatMessage(
      DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.unknownPublisher
    );

  const [previewed, setPreviewed] = useState(null);

  const closePreview = useCallback(() => setPreviewed(null), []);

  const handleImport = async (published) => {
    closePreview();
    confirmAction({ status: QUERY_STATUS.LOADING });
    const created = await onImport(published.template_id);
    confirmAction({
      status: created ? QUERY_STATUS.SUCCESS : QUERY_STATUS.ERROR,
      messages: {
        ...DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.templateImportPublic,
        values: { name: created?.name || published.name },
      },
    });
  };

  return (
    <section
      className="delivery-note-public-templates"
      id="delivery-note-public-templates"
    >
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage {...DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.title} />
        }
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={
          <FormattedMessage
            {...DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.subtitle}
          />
        }
      />

      {loadStatus === QUERY_STATUS.LOADING && (
        <LoaderV2 id="delivery-note-public-templates-loader" />
      )}

      {loadStatus === QUERY_STATUS.ERROR && (
        <Typography
          className="text-3 alizarin-crimson"
          type="p"
          label={
            <FormattedMessage
              {...DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.loadError}
            />
          }
        />
      )}

      {loadStatus === QUERY_STATUS.SUCCESS && !publicTemplates?.length && (
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...(hasActiveFilters
                ? DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.noMatch
                : DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.empty)}
            />
          }
        />
      )}

      <ul className="delivery-note-public-templates__list">
        {(publicTemplates || []).map((published) => {
          const counts = countColumnsByType(published.columns);
          return (
            <li
              className="delivery-note-public-templates__item"
              key={published.template_id}
            >
              <div className="delivery-note-public-templates__identity">
                <div className="delivery-note-public-templates__name">
                  <Typography
                    className="title-5 deep-purple"
                    type="span"
                    label={published.name}
                  />
                  <span
                    className="delivery-note-public-templates__badge"
                    data-testid={`delivery-note-public-template-badge-${published.template_id}`}
                  >
                    <FormattedMessage
                      {...DELIVERY_NOTE_PUBLIC_TEMPLATE_MESSAGES.publishedBy}
                      values={{ name: publisherName(published) }}
                    />
                  </span>
                </div>
                <Typography
                  className="text-3 violet"
                  type="span"
                  label={
                    <FormattedMessage
                      {...DELIVERY_NOTE_TEMPLATE_ITEM_DETAIL}
                      values={{
                        issuer: published.issuer?.company_name || "—",
                        recipient: published.recipient?.company_name || "—",
                        simple: counts.simple,
                        linked: counts.linked,
                      }}
                    />
                  }
                />
              </div>
              <div className="delivery-note-public-templates__buttons">
                <button
                  type="button"
                  className="delivery-note-public-templates__action"
                  onClick={() => setPreviewed(published)}
                  aria-label={previewLabel}
                  title={previewLabel}
                  data-testid={`delivery-note-public-template-preview-${published.template_id}`}
                >
                  <Icon icon="eye" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-public-templates__action"
                  onClick={() => handleImport(published)}
                  disabled={isBusy}
                  aria-label={importLabel}
                  title={importLabel}
                  data-testid={`delivery-note-public-template-import-${published.template_id}`}
                >
                  <Icon icon="import" color="violet" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {previewed && (
        <DeliveryNoteTemplatePreviewModal
          template={previewed}
          onClose={closePreview}
          onImport={handleImport}
          isImporting={isBusy}
        />
      )}
    </section>
  );
};

DeliveryNotePublicTemplateList.propTypes = {
  publicTemplates: PropTypes.array,
  hasActiveFilters: PropTypes.bool,
  loadStatus: PropTypes.string,
  isBusy: PropTypes.bool,
  onImport: PropTypes.func.isRequired,
};

export default DeliveryNotePublicTemplateList;
